import {
  FileText,
  Expand,
  Video,
  Volume2,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Menu,
  X,
  CheckCircle2,
  ClipboardCheck,
  Code,
  Pen,
  Eraser,
  RotateCcw,
  Palette,
  Copy,
  Check,
  Download,
  Play,
  AlertCircle,
  Circle,
  Sparkles,
  Send,
  Loader2,
} from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeHighlight from "rehype-highlight";
import "katex/dist/katex.min.css";
import { ApiService } from "../../../../services/api";
import AlertModal from "../../../../components/modals/AlertModal";
import SolutionVideoModal from "../homework/SolutionVideoModal";
import Modal from "../../../../components/ui/Modal";
import { CustomVideoPlayer } from "../../../../components/video/CustomVideoPlayer";
import DeficiencyTutorPanel from "../../../../components/panels/DeficiencyTutorPanel";

interface QuestionAiChatMessage {
  id: string;
  type: "user" | "assistant" | "system";
  content: string;
}

export default function ContentViewer({
  content,
  onExamStatusChange,
  onUserStatusLoad,
  onTrackingUpdate,
  isParentSidebarOpen,
}: {
  content: any;
  onExamStatusChange?: (status: "IN_PROGRESS" | "COMPLETED") => void;
  onUserStatusLoad?: (userStatus: any) => void;
  onTrackingUpdate?: (contentId: string) => void;
  isParentSidebarOpen?: boolean;
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [questions, setQuestions] = useState<any[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<any>({});
  const [isExamCompleted, setIsExamCompleted] = useState(false);
  const [isAnswerKeyVisible, setIsAnswerKeyVisible] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isDrawingPanelCollapsed, setIsDrawingPanelCollapsed] = useState(false);
  const [isSolutionVideoModalOpen, setIsSolutionVideoModalOpen] = useState(false);
  const [selectedQuestionForSolution, setSelectedQuestionForSolution] = useState<any>(null);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [contentGains, setContentGains] = useState<any[]>([]);
  const [isTutorModalOpen, setIsTutorModalOpen] = useState(false);
  const [selectedGainForTutor, setSelectedGainForTutor] = useState<string | null>(null);
  const [isExamMutationPending, setIsExamMutationPending] = useState(false);
  const [questionAiPanelOpen, setQuestionAiPanelOpen] = useState(false);
  const [questionAiInput, setQuestionAiInput] = useState("");
  const [questionAiLoading, setQuestionAiLoading] = useState(false);
  const [questionAiLoadingStep, setQuestionAiLoadingStep] = useState(0);
  const [questionAiHistoryByQuestion, setQuestionAiHistoryByQuestion] = useState<Record<string, QuestionAiChatMessage[]>>({});
  const questionAiScrollRef = useRef<HTMLDivElement | null>(null);
  const examMutationLockRef = useRef(false);

  const calculateExamStats = () => {
    const userStatus = (content as any)?.userStatus;
    const backendStats = userStatus?.stats;

    if (!isExamCompleted || !backendStats) {
      return {
        totalQuestions: 0,
        correctAnswers: 0,
        wrongAnswers: 0,
        unanswered: 0,
        successRate: 0,
      };
    }

    const totalQuestions = backendStats.totalQuestions || 0;
    const correctAnswers = backendStats.totalCorrect || 0;
    const wrongAnswers = backendStats.totalWrong || 0;
    const unanswered = backendStats.totalBlank || 0;
    const successRate = backendStats.percent || 0;

    return {
      totalQuestions,
      correctAnswers,
      wrongAnswers,
      unanswered,
      successRate,
    };
  };

  const getImageHeight = () => {
    if (isFullscreen) {
      if (isMobilePhone()) {
        if (!isAnswerKeyVisible && isParentSidebarOpen === false) {
          return "calc(100vh - 350px)";
        } else if (!isAnswerKeyVisible || isSidebarCollapsed) {
          return "calc(100vh - 400px)";
        }
        return "calc(100vh - 450px)";
      } else {
        if (questionAiPanelOpen) {
          return "clamp(300px, calc(100vh - 590px), 470px)";
        }
        if (!isAnswerKeyVisible && isParentSidebarOpen === false) {
          return "calc(100vh - 300px)";
        } else if (!isAnswerKeyVisible || isSidebarCollapsed) {
          return "calc(100vh - 350px)";
        }
        return "calc(100vh - 400px)";
      }
    } else {
      if (!isAnswerKeyVisible && isParentSidebarOpen === false) {
        return "600px";
      } else if (!isAnswerKeyVisible || isSidebarCollapsed) {
        return "500px";
      }
      return "400px";
    }
  };

  const handleSolutionVideoToggle = (question: any) => {
    setSelectedQuestionForSolution(question);
    setIsSolutionVideoModalOpen(true);
  };

  const handleCloseSolutionModal = () => {
    setIsSolutionVideoModalOpen(false);
    setSelectedQuestionForSolution(null);
  };

  const [drawingTool, setDrawingTool] = useState<"pen" | "eraser">("pen");
  const [penSize, setPenSize] = useState(3);
  const [eraserSize, setEraserSize] = useState(10);
  const [penColor, setPenColor] = useState("#0077b6");
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [isDrawingModeEnabled, setIsDrawingModeEnabled] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const whiteboardRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const [imageZoom, setImageZoom] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement | null>(null);
  const imageContainerRef = useRef<HTMLDivElement | null>(null);

  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const [touchStartPos, setTouchStartPos] = useState({ x: 0, y: 0 });
  const [hasTouchMoved, setHasTouchMoved] = useState(false);


  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info" as "success" | "error" | "warning" | "info",
  });
  const [isExamActionModalOpen, setIsExamActionModalOpen] = useState(false);

  const colorOptions = [
    { name: "Siyah", value: "#1a1a2e" },
    { name: "Kırmızı", value: "#e63946" },
    { name: "Mavi", value: "#0077b6" },
    { name: "Yeşil", value: "#2a9d8f" },
    { name: "Turuncu", value: "#f4a261" },
    { name: "Mor", value: "#7209b7" },
    { name: "Sarı", value: "#fca311" },
    { name: "Pembe", value: "#e91e63" },
  ];

  useEffect(() => {
    if (canvasRef.current && isDrawingModeEnabled) {
      const canvas = canvasRef.current;

      const handleTouchStart = (e: TouchEvent) => {
        e.preventDefault();
        setIsDrawing(true);

        const touch = e.touches[0];
        const rect = canvas.getBoundingClientRect();

        if (isFullscreen) {
          const x = touch.clientX - rect.left + window.scrollX;
          const y = touch.clientY - rect.top + window.scrollY;

          if (isInRestrictedArea(touch.clientX, touch.clientY)) {
            return;
          }

          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.beginPath();
            ctx.moveTo(x, y);
          }
        } else {
          const scaleX = canvas.width / (rect.width * window.devicePixelRatio);
          const scaleY = canvas.height / (rect.height * window.devicePixelRatio);
          const x = (touch.clientX - rect.left) / scaleX;
          const y = (touch.clientY - rect.top) / scaleY;

          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.beginPath();
            ctx.moveTo(x, y);
          }
        }
      };

      const handleTouchMove = (e: TouchEvent) => {
        if (!isDrawing) return;
        e.preventDefault();

        const touch = e.touches[0];
        const rect = canvas.getBoundingClientRect();

        if (isFullscreen) {
          const x = touch.clientX - rect.left + window.scrollX;
          const y = touch.clientY - rect.top + window.scrollY;

          if (isInRestrictedArea(touch.clientX, touch.clientY)) {
            setIsDrawing(false);
            return;
          }

          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.lineTo(x, y);
            if (drawingTool === "pen") {
              ctx.globalCompositeOperation = "source-over";
              ctx.strokeStyle = penColor;
              ctx.lineWidth = penSize;
            } else {
              ctx.globalCompositeOperation = "destination-out";
              ctx.lineWidth = eraserSize;
            }
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            ctx.stroke();
          }
        } else {
          const scaleX = canvas.width / (rect.width * window.devicePixelRatio);
          const scaleY = canvas.height / (rect.height * window.devicePixelRatio);
          const x = (touch.clientX - rect.left) / scaleX;
          const y = (touch.clientY - rect.top) / scaleY;

          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.lineTo(x, y);
            if (drawingTool === "pen") {
              ctx.globalCompositeOperation = "source-over";
              ctx.strokeStyle = penColor;
              ctx.lineWidth = penSize;
            } else {
              ctx.globalCompositeOperation = "destination-out";
              ctx.lineWidth = eraserSize;
            }
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            ctx.stroke();
          }
        }
      };

      const handleTouchEnd = () => {
        setIsDrawing(false);
      };

      canvas.addEventListener("touchstart", handleTouchStart, {
        passive: false,
      });
      canvas.addEventListener("touchmove", handleTouchMove, { passive: false });
      canvas.addEventListener("touchend", handleTouchEnd, { passive: false });
      canvas.addEventListener("touchcancel", handleTouchEnd, {
        passive: false,
      });

      return () => {
        canvas.removeEventListener("touchstart", handleTouchStart);
        canvas.removeEventListener("touchmove", handleTouchMove);
        canvas.removeEventListener("touchend", handleTouchEnd);
        canvas.removeEventListener("touchcancel", handleTouchEnd);
      };
    }
  }, [isDrawingModeEnabled, isFullscreen, isDrawing, drawingTool, penColor, penSize, eraserSize]);

  useEffect(() => {
    if (whiteboardRef.current && showWhiteboard) {
      const canvas = whiteboardRef.current;

      const handleTouchStart = (e: TouchEvent) => {
        e.preventDefault();
        setIsDrawing(true);

        const touch = e.touches[0];
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / (rect.width * window.devicePixelRatio);
        const scaleY = canvas.height / (rect.height * window.devicePixelRatio);
        const x = (touch.clientX - rect.left) / scaleX;
        const y = (touch.clientY - rect.top) / scaleY;

        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.beginPath();
          ctx.moveTo(x, y);
        }
      };

      const handleTouchMove = (e: TouchEvent) => {
        if (!isDrawing) return;
        e.preventDefault();

        const touch = e.touches[0];
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / (rect.width * window.devicePixelRatio);
        const scaleY = canvas.height / (rect.height * window.devicePixelRatio);
        const x = (touch.clientX - rect.left) / scaleX;
        const y = (touch.clientY - rect.top) / scaleY;

        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.lineTo(x, y);
          if (drawingTool === "pen") {
            ctx.globalCompositeOperation = "source-over";
            ctx.strokeStyle = penColor;
            ctx.lineWidth = penSize;
          } else {
            ctx.globalCompositeOperation = "destination-out";
            ctx.lineWidth = eraserSize;
          }
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.stroke();
        }
      };

      const handleTouchEnd = () => {
        setIsDrawing(false);
      };

      canvas.addEventListener("touchstart", handleTouchStart, {
        passive: false,
      });
      canvas.addEventListener("touchmove", handleTouchMove, { passive: false });
      canvas.addEventListener("touchend", handleTouchEnd, { passive: false });
      canvas.addEventListener("touchcancel", handleTouchEnd, {
        passive: false,
      });

      return () => {
        canvas.removeEventListener("touchstart", handleTouchStart);
        canvas.removeEventListener("touchmove", handleTouchMove);
        canvas.removeEventListener("touchend", handleTouchEnd);
        canvas.removeEventListener("touchcancel", handleTouchEnd);
      };
    }
  }, [showWhiteboard, isDrawing, drawingTool, penColor, penSize, eraserSize]);

  useEffect(() => {
    const onFsChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);

      if (!isNowFullscreen) {
        setQuestionAiPanelOpen(false);
      }

      if (!isNowFullscreen && (content.type === "FILE" || content.type === "PDF")) {
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport && isMobilePhone()) {
          viewport.setAttribute("content", "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no");
        }
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, [content.type]);

  const isInRestrictedArea = (x: number, y: number) => {
    try {
      const element = document.elementFromPoint(x, y);
      if (!element) return false;

      let currentElement: Element | null = element;
      while (currentElement && currentElement !== document.body) {
        const className = currentElement.className || "";

        if (typeof className === "string") {
          if (
            className.includes("drawing-restricted-left-panel") ||
            className.includes("drawing-restricted-header") ||
            className.includes("drawing-restricted-nav") ||
            className.includes("drawing-restricted-buttons")
          ) {
            return true;
          }
        }

        if (currentElement.closest) {
          if (
            currentElement.closest(".drawing-restricted-left-panel") ||
            currentElement.closest(".drawing-restricted-header") ||
            currentElement.closest(".drawing-restricted-nav") ||
            currentElement.closest(".drawing-restricted-buttons")
          ) {
            return true;
          }
        }

        currentElement = currentElement.parentElement;
      }

      return false;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const fetchQuestions = async () => {
      const _content = await ApiService.getContent(content.lessonId, content.sectionId, content.id);

      Object.assign(content, _content);

      const rawTopLevel = Array.isArray((_content as any)?.questions) ? (_content as any).questions : [];
      const rawInMetadata = Array.isArray((_content as any)?.metadata?.questions) ? (_content as any).metadata.questions : [];

      const rawQuestions: any[] = rawTopLevel.length > 0 ? rawTopLevel : rawInMetadata;

      const normalizedQuestions = rawQuestions.map((q: any, idx: number) => {
        const id = q.id || q.qid || String(idx);

        const originalOptions = Array.isArray(q.options) ? q.options : [];
        const options = originalOptions.map((opt: any) => {
          if (typeof opt === "object" && opt !== null) {
            return opt.text || opt.value || opt.label || opt.option || String(opt);
          }
          return String(opt);
        });

        let correctOption = "";
        if (q.correctOption !== undefined) {
          if (typeof q.correctOption === "object" && q.correctOption !== null) {
            correctOption = q.correctOption.text || q.correctOption.value || String(q.correctOption);
          } else {
            correctOption = String(q.correctOption);
          }
        } else if (typeof q.correctAnswer === "string") {
          correctOption = q.correctAnswer;
        } else if (Array.isArray(q.correctAnswer) && q.correctAnswer.length > 0) {
          const firstAnswer = q.correctAnswer[0];
          if (typeof firstAnswer === "object" && firstAnswer !== null) {
            correctOption = firstAnswer.text || firstAnswer.value || String(firstAnswer);
          } else {
            correctOption = String(firstAnswer);
          }
        }

        return {
          ...q,
          id,
          originalId: q.id || q.qid || id,
          question: q.content || q.question || "",
          options,
          originalOptions,
          correctOption,
          order: q.order !== undefined ? q.order : 0,
          createdAt: q.createdAt || new Date().toISOString(),
        };
      });

      const sortedQuestions = normalizedQuestions.sort((a: any, b: any) => {
        if (a.order !== b.order) {
          return a.order - b.order;
        }
        if (a.createdAt !== b.createdAt) {
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        }
        return a.id.localeCompare(b.id);
      });

      setQuestions(sortedQuestions);
      setCurrentQuestionIndex(0);
      setSelectedAnswers({});

      const userStatus = (_content as any)?.userStatus;
      if (userStatus && typeof userStatus === "object") {
        setIsExamCompleted(userStatus.status === "COMPLETED");
        onExamStatusChange?.(userStatus.status);
        onUserStatusLoad?.(userStatus);
        if (Array.isArray(userStatus.answers)) {
          const answers = userStatus.answers.reduce((acc: any, answer: any) => {
            acc[answer.lessonSectionExamQuestionId] = answer.answer;
            return acc;
          }, {});
          setSelectedAnswers(answers);
        }
      } else {
        setIsExamCompleted(false);
      }
      setIsAnswerKeyVisible(true);
    };
    if (content.type === "EXAM") {
      fetchQuestions();
    }

    const sendUserTracking = async () => {
      await ApiService.sendUserTracking(content.id, { duration: 0 });
      onTrackingUpdate?.(content.id);
    };
    sendUserTracking();
  }, [content, onExamStatusChange, onUserStatusLoad]);

  useEffect(() => {
    const activeQuestion = questions[currentQuestionIndex];
    if (questionAiPanelOpen && activeQuestion?.metadata?.source !== "question_bank_indesign") {
      setQuestionAiPanelOpen(false);
    }
  }, [currentQuestionIndex, questionAiPanelOpen, questions]);

  useEffect(() => {
    if (!questionAiPanelOpen) return;
    requestAnimationFrame(() => {
      if (questionAiScrollRef.current) {
        questionAiScrollRef.current.scrollTop = questionAiScrollRef.current.scrollHeight;
      }
    });
  }, [questionAiPanelOpen, questionAiHistoryByQuestion, currentQuestionIndex]);

  useEffect(() => {
    if (!questionAiLoading) {
      setQuestionAiLoadingStep(0);
      return;
    }

    const loadingTimer = window.setInterval(() => {
      setQuestionAiLoadingStep((step) => (step + 1) % 4);
    }, 1800);

    return () => window.clearInterval(loadingTimer);
  }, [questionAiLoading]);

  const resetImageView = () => {
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
  };

  useEffect(() => {
    resetImageView();
  }, [currentQuestionIndex]);

  useEffect(() => {
    const fetchContentGains = async () => {
      if (!isExamCompleted || content.type !== "EXAM") return;
      try {
        const response = await ApiService.getUserGains({
          contentId: content.id,
          isResolved: false,
        });
        const gains = response?.data || [];
        setContentGains(gains);
      } catch (err) {
        setContentGains([]);
      }
    };
    fetchContentGains();
  }, [isExamCompleted, content.id, content.type]);

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (isDrawingModeEnabled) return;
    if (hasDragged || hasTouchMoved) return;

    e.stopPropagation();
    try {
      e.preventDefault();
    } catch (error) { }

    const maxZoom = getMaxZoom();
    const newZoom = imageZoom === 1 ? maxZoom : 1;

    if (newZoom > 1 && imageRef.current && imageContainerRef.current) {
      const containerRect = imageContainerRef.current.getBoundingClientRect();

      const clickX = e.clientX - containerRect.left;
      const clickY = e.clientY - containerRect.top;

      const containerCenterX = containerRect.width / 2;
      const containerCenterY = containerRect.height / 2;

      const deltaX = clickX - containerCenterX;
      const deltaY = clickY - containerCenterY;

      const offsetX = -deltaX * (newZoom - 1);
      const offsetY = -deltaY * (newZoom - 1);

      setImagePosition({ x: offsetX, y: offsetY });
    } else if (newZoom === 1) {
      setImagePosition({ x: 0, y: 0 });
    }

    setImageZoom(newZoom);
  };

  const isMobilePhone = () => {
    return window.innerWidth <= 768;
  };

  const getMaxZoom = useCallback(() => {
    return isMobilePhone() ? 1.3 : 2;
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const maxZoom = getMaxZoom();
      if (imageZoom > maxZoom) {
        setImageZoom(maxZoom);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [imageZoom, getMaxZoom]);

  const handleImageMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    if (isDrawingModeEnabled || imageZoom === 1) return;

    setIsDragging(true);
    setHasDragged(false);
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y,
    });
  };

  const handleImageMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!isDragging || isDrawingModeEnabled || imageZoom === 1) return;

    const deltaX = Math.abs(e.clientX - dragStartPos.x);
    const deltaY = Math.abs(e.clientY - dragStartPos.y);

    if (deltaX > 5 || deltaY > 5) {
      setHasDragged(true);
    }

    setImagePosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleImageMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (imageRef.current) {
      const image = imageRef.current;

      const handleImageTouchStart = (e: TouchEvent) => {
        if (isDrawingModeEnabled) return;

        const touch = e.touches[0];
        setIsTouching(true);
        setHasTouchMoved(false);
        setTouchStartPos({ x: touch.clientX, y: touch.clientY });

        if (imageZoom > 1) {
          e.preventDefault();
          e.stopPropagation();
          setDragStart({
            x: touch.clientX - imagePosition.x,
            y: touch.clientY - imagePosition.y,
          });
        }
      };

      const handleImageTouchMove = (e: TouchEvent) => {
        if (!isTouching || isDrawingModeEnabled) return;

        const touch = e.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStartPos.x);
        const deltaY = Math.abs(touch.clientY - touchStartPos.y);

        if (imageZoom > 1) {
          e.preventDefault();
          e.stopPropagation();
        }

        if (deltaX > 15 || deltaY > 15) {
          setHasTouchMoved(true);
          e.preventDefault();
          e.stopPropagation();
        }

        if (imageZoom > 1 && (deltaX > 10 || deltaY > 10)) {
          setImagePosition({
            x: touch.clientX - dragStart.x,
            y: touch.clientY - dragStart.y,
          });
        }
      };

      const handleImageTouchEnd = (e: TouchEvent) => {
        if (!isTouching || isDrawingModeEnabled) return;

        e.preventDefault();
        setIsTouching(false);

        if (!hasTouchMoved) {
          const touch = e.changedTouches[0];
          const maxZoom = getMaxZoom();
          const newZoom = imageZoom === 1 ? maxZoom : 1;

          if (newZoom > 1 && imageRef.current && imageContainerRef.current) {
            const containerRect = imageContainerRef.current.getBoundingClientRect();
            const touchX = touch.clientX - containerRect.left;
            const touchY = touch.clientY - containerRect.top;

            const containerCenterX = containerRect.width / 2;
            const containerCenterY = containerRect.height / 2;

            const deltaX = touchX - containerCenterX;
            const deltaY = touchY - containerCenterY;

            const offsetX = -deltaX * (newZoom - 1);
            const offsetY = -deltaY * (newZoom - 1);

            setImagePosition({ x: offsetX, y: offsetY });
          } else if (newZoom === 1) {
            setImagePosition({ x: 0, y: 0 });
          }

          setImageZoom(newZoom);
        }
      };

      image.addEventListener("touchstart", handleImageTouchStart, {
        passive: false,
      });
      image.addEventListener("touchmove", handleImageTouchMove, {
        passive: false,
      });
      image.addEventListener("touchend", handleImageTouchEnd, {
        passive: false,
      });

      return () => {
        image.removeEventListener("touchstart", handleImageTouchStart);
        image.removeEventListener("touchmove", handleImageTouchMove);
        image.removeEventListener("touchend", handleImageTouchEnd);
      };
    }
  }, [isDrawingModeEnabled, isTouching, hasTouchMoved, touchStartPos, imageZoom, imagePosition, dragStart, getMaxZoom]);

  useEffect(() => {
    const resizeCanvas = () => {
      if (canvasRef.current && isDrawingModeEnabled) {
        const canvas = canvasRef.current;

        if (isFullscreen) {
          const parent = canvas.parentElement;
          if (parent) {
            const width = parent.scrollWidth;
            const height = Math.max(parent.scrollHeight, window.innerHeight);
            canvas.width = width * window.devicePixelRatio;
            canvas.height = height * window.devicePixelRatio;
            canvas.style.width = width + "px";
            canvas.style.height = height + "px";

            const ctx = canvas.getContext("2d");
            if (ctx) {
              ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
            }
          }
        } else {
          const rect = canvas.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            canvas.width = rect.width * window.devicePixelRatio;
            canvas.height = rect.height * window.devicePixelRatio;
            canvas.style.width = rect.width + "px";
            canvas.style.height = rect.height + "px";

            const ctx = canvas.getContext("2d");
            if (ctx) {
              ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
            }
          }
        }
      }

      if (whiteboardRef.current) {
        const canvas = whiteboardRef.current;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * window.devicePixelRatio;
        canvas.height = rect.height * window.devicePixelRatio;
        canvas.style.width = rect.width + "px";
        canvas.style.height = rect.height + "px";
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        }
      }
    };

    if (isDrawingModeEnabled || showWhiteboard) {
      resizeCanvas();
      window.addEventListener("resize", resizeCanvas);
      window.addEventListener("orientationchange", resizeCanvas);

      return () => {
        window.removeEventListener("resize", resizeCanvas);
        window.removeEventListener("orientationchange", resizeCanvas);
      };
    }
  }, [currentQuestionIndex, isDrawingModeEnabled, showWhiteboard, isFullscreen]);

  const renderFile = () => {
    const fileUrl = content.media?.url || "";
    return (
      <div className={`w-full bg-black ${isFullscreen ? "h-screen" : ""}`}>
        <div
          className={`relative w-full ${isFullscreen ? "h-screen" : ""}`}
          style={isFullscreen ? { height: "100vh" } : { paddingBottom: "56.25%" }}>
          <iframe
            src={fileUrl}
            className={`absolute top-0 left-0 w-full h-full ${isFullscreen ? "border-0" : ""}`}
            title={`content-file-${content?.id || "unknown"}`}
            style={{
              ...(isFullscreen && {
                width: "100vw",
                height: "100vh",
                maxWidth: "100vw",
                maxHeight: "100vh",
                border: "none",
                margin: 0,
                padding: 0,
              }),
              WebkitOverflowScrolling: "touch",
              overflow: "auto",
              touchAction: "pan-x pan-y pinch-zoom",
            }}
            allow="fullscreen"
            allowFullScreen
          />
        </div>
      </div>
    );
  };

  const renderExam = () => {
    const currentQuestion = questions[currentQuestionIndex];
    const isCurrentQuestionFromQuestionBank = currentQuestion?.metadata?.source === "question_bank_indesign";
    const currentQuestionAiMessages = currentQuestion?.id ? questionAiHistoryByQuestion[currentQuestion.id] || [] : [];
    const questionAiLoadingTexts = [
      "Düşünüyor...",
      "Soruyu inceliyor...",
      "Çözüm yolunu kuruyor...",
      "Yanıt hazırlanıyor...",
    ];
    const fullscreenDesktopNavClass = isFullscreen && !isMobilePhone()
      ? `fixed bottom-0 left-0 ${isAnswerKeyVisible ? (isSidebarCollapsed ? "md:left-12" : "md:left-64 lg:left-56") : "md:left-0"} ${questionAiPanelOpen ? "right-0 md:right-[390px] lg:right-[430px]" : "right-0"} p-3 z-50 bg-gray-100/95 border-t border-slate-200 backdrop-blur`
      : "";

    const handleQuestionAiButtonClick = async () => {
      if (questionAiPanelOpen) {
        setQuestionAiPanelOpen(false);
        return;
      }

      if (!document.fullscreenElement) {
        const contentViewer = document.getElementById("content-viewer");
        if (!contentViewer) return;

        try {
          await contentViewer.requestFullscreen();
          setIsFullscreen(true);
        } catch {
          return;
        }
      }

      setQuestionAiPanelOpen(true);
    };

    const buildSelectedAnswersArray = () =>
      Object.entries(selectedAnswers).map(([questionId, answer]) => {
        const q = questions.find((qq: any) => String(qq.id) === String(questionId)) as any;
        return {
          questionId: String(q?.id ?? questionId),
          answer: String(answer),
        };
      });

    const updateQuestionAiMessages = (questionId: string, updater: (messages: QuestionAiChatMessage[]) => QuestionAiChatMessage[]) => {
      setQuestionAiHistoryByQuestion((prev) => ({
        ...prev,
        [questionId]: updater(prev[questionId] || []),
      }));
    };

    const sendQuestionAiMessage = async () => {
      const textToSend = questionAiInput.trim();
      if (!currentQuestion?.id || !textToSend || questionAiLoading) return;

      const userMessage: QuestionAiChatMessage = {
        id: crypto.randomUUID(),
        type: "user",
        content: textToSend,
      };
      const nextHistory = [...currentQuestionAiMessages, userMessage];

      updateQuestionAiMessages(currentQuestion.id, () => nextHistory);
      setQuestionAiInput("");
      setQuestionAiLoading(true);

      try {
        const response = await ApiService.sendLessonQuestionAssist({
          lessonContentId: content.id,
          questionId: currentQuestion.id,
          message: textToSend,
          history: nextHistory
            .filter((item) => item.type === "user" || item.type === "assistant")
            .map((item) => ({ type: item.type as "user" | "assistant", content: item.content })),
        });

        const assistantMessage: QuestionAiChatMessage = {
          id: crypto.randomUUID(),
          type: "assistant",
          content: response?.output || "Yanıt oluşturulamadı. Lütfen tekrar deneyin.",
        };
        updateQuestionAiMessages(currentQuestion.id, (messages) => [...messages, assistantMessage]);
      } catch (error: any) {
        const systemMessage: QuestionAiChatMessage = {
          id: crypto.randomUUID(),
          type: "system",
          content: error?.message || "AI yardım alınırken bir hata oluştu.",
        };
        updateQuestionAiMessages(currentQuestion.id, (messages) => [...messages, systemMessage]);
      } finally {
        setQuestionAiLoading(false);
      }
    };

    const renderQuestionAiPanel = () => {
      if (!questionAiPanelOpen || !isCurrentQuestionFromQuestionBank) return null;

      return (
        <>
        <style>{`
          .question-ai-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #94a3b8 #f8fafc;
          }
          .question-ai-scrollbar::-webkit-scrollbar {
            width: 8px;
          }
          .question-ai-scrollbar::-webkit-scrollbar-track {
            background: #f8fafc;
          }
          .question-ai-scrollbar::-webkit-scrollbar-thumb {
            background: #94a3b8;
            border-radius: 999px;
            border: 2px solid #f8fafc;
          }
        `}</style>
        <aside
          className={`drawing-restricted-buttons bg-white border-slate-200 shadow-xl ring-1 ring-slate-200/70 ${
            isFullscreen
              ? "fixed bottom-0 left-0 right-0 z-[60] h-[58vh] md:sticky md:left-auto md:right-auto md:top-0 md:bottom-auto md:h-[calc(100vh-80px)] md:w-[390px] lg:w-[430px]"
              : "fixed bottom-0 left-0 right-0 z-[60] h-[58vh] md:sticky md:top-4 md:self-start md:h-[calc(100vh-120px)] md:w-[320px] lg:w-[360px]"
          } md:shrink-0 md:border-l border-t md:border-t-0 flex min-h-0 flex-col overflow-hidden`}>
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 min-w-0">
                <div className="h-8 w-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="truncate">AI Yardım</div>
                  <div className="text-[11px] font-medium text-slate-500">Soru {currentQuestionIndex + 1}</div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`hidden sm:inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                isExamCompleted ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"
              }`}>
                {isExamCompleted ? "Analiz" : "İpucu"}
              </span>
            <button
              type="button"
              onClick={() => setQuestionAiPanelOpen(false)}
              className="p-2 text-slate-500 hover:text-slate-800 hover:bg-white rounded-lg transition-colors"
              aria-label="AI panelini kapat">
              <X className="w-4 h-4" />
            </button>
            </div>
          </div>

          <div ref={questionAiScrollRef} className="question-ai-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-3 bg-white">
            {currentQuestionAiMessages.length === 0 && (
              <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
                {isExamCompleted
                  ? "Bu soru için çözüm yolunu, yaptığın hatayı veya çeldiricileri sorabilirsin."
                  : "Bu soru için ipucu isteyebilirsin. Test bitmeden doğru cevabı paylaşmam."}
              </div>
            )}

            {currentQuestionAiMessages.map((item) => (
              <div
                key={item.id}
                className={`w-fit max-w-[88%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                  item.type === "user"
                    ? "ml-auto bg-blue-600 text-white"
                    : item.type === "system"
                    ? "mr-auto bg-red-50 text-red-700 border border-red-100"
                    : "mr-auto bg-slate-50 text-slate-800 border border-slate-200 shadow-sm"
                }`}>
                {item.type === "assistant" ? (
                  <div className="space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_code]:rounded [&_code]:bg-slate-100 [&_code]:px-1">
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex, rehypeHighlight]}>
                      {item.content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap">{item.content}</div>
                )}
              </div>
            ))}

            {questionAiLoading && (
              <div className="mr-8 rounded-lg bg-slate-50 text-slate-600 border border-slate-200 px-3 py-2 text-sm flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {questionAiLoadingTexts[questionAiLoadingStep]}
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-slate-200 p-3 bg-slate-50">
            <div className="flex items-end gap-2">
              <textarea
                value={questionAiInput}
                onChange={(event) => setQuestionAiInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void sendQuestionAiMessage();
                  }
                }}
                placeholder={isExamCompleted ? "Bu soruda neyi yanlış yaptım?" : "Bu soru için ipucu ver..."}
                rows={2}
                className="min-h-[44px] max-h-28 flex-1 resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
              <button
                type="button"
                onClick={() => void sendQuestionAiMessage()}
                disabled={!questionAiInput.trim() || questionAiLoading}
                className="h-11 w-11 shrink-0 rounded-lg bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="AI mesajı gönder">
                {questionAiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </aside>
        </>
      );
    };

    const refreshContentAfterExamMutation = async (nextStatus: "IN_PROGRESS" | "COMPLETED") => {
      const updatedContent = await ApiService.getContent(content.lessonId, content.sectionId, content.id);

      if (updatedContent && updatedContent.userStatus) {
        Object.assign(content, updatedContent);
      }

      setIsExamCompleted(nextStatus === "COMPLETED");
      onExamStatusChange?.(nextStatus);
      onUserStatusLoad?.(
        updatedContent?.userStatus || {
          status: nextStatus,
          answers: selectedAnswers,
        }
      );
      onTrackingUpdate?.(content.id);
    };

    const handleExamComplete = async () => {
      if (isExamCompleted) {
        setAlertModal({
          isOpen: true,
          title: "Sınav Zaten Tamamlandı",
          message: "Bu sınav daha önce tamamlanmış. Tekrar tamamlayamazsınız.",
          type: "error",
        });
        return;
      }

      const answeredQuestionCount = Object.keys(selectedAnswers).length;
      if (answeredQuestionCount === 0) {
        setAlertModal({
          isOpen: true,
          title: "Hiç Soru Cevaplanmamış",
          message: "Sınavı tamamlamak için en az bir soruyu cevaplamanız gerekiyor.",
          type: "warning",
        });
        return;
      }

      if (isFullscreen) {
        exitFullscreen();
      }
      setIsExamActionModalOpen(true);
    };

    const handleSaveAndFinish = async () => {
      if (examMutationLockRef.current) return;
      examMutationLockRef.current = true;
      setIsExamMutationPending(true);
      try {
        const selectedAnswersArray = buildSelectedAnswersArray();
        await ApiService.completeExam(content.id, {
          questions: selectedAnswersArray,
        });
        setIsExamActionModalOpen(false);
        await refreshContentAfterExamMutation("COMPLETED");
      } catch (error: any) {
        setAlertModal({
          isOpen: true,
          title: "Hata",
          message: error?.message || "Sınav tamamlanırken bir hata oluştu.",
          type: "error",
        });
      } finally {
        examMutationLockRef.current = false;
        setIsExamMutationPending(false);
      }
    };

    const handleSaveAndExit = async () => {
      if (examMutationLockRef.current) return;
      examMutationLockRef.current = true;
      setIsExamMutationPending(true);
      try {
        const selectedAnswersArray = buildSelectedAnswersArray();
        await ApiService.saveExam(content.id, {
          questions: selectedAnswersArray,
        });
        setIsExamActionModalOpen(false);
        await refreshContentAfterExamMutation("IN_PROGRESS");
        setAlertModal({
          isOpen: true,
          title: "Kaydedildi",
          message: "Cevaplarınız kaydedildi. Daha sonra kaldığınız yerden devam edebilirsiniz.",
          type: "success",
        });
      } catch (error: any) {
        setAlertModal({
          isOpen: true,
          title: "Hata",
          message: error?.message || "Sınav kaydedilirken bir hata oluştu.",
          type: "error",
        });
      } finally {
        examMutationLockRef.current = false;
        setIsExamMutationPending(false);
      }
    };

    const handleAnswerSelect = (questionId: string, option: string) => {
      if (!isExamCompleted) {
        setSelectedAnswers((prev: any) => {
          if ((prev as any)[questionId] === option) {
            const newAnswers = { ...prev };
            delete (newAnswers as any)[questionId];
            return newAnswers;
          }

          const newAnswers = {
            ...prev,
            [questionId]: option,
          };
          if (Object.keys(prev).length === 0) {
            onExamStatusChange?.("IN_PROGRESS");
            onUserStatusLoad?.({ status: "IN_PROGRESS", answers: newAnswers });
          }
          return newAnswers;
        });
      }
    };

    const goToQuestion = (index: number) => {
      if (index >= 0 && index < questions.length) {
        setCurrentQuestionIndex(index);
      }
    };

    const goToPreviousQuestion = () => {
      if (currentQuestionIndex > 0) {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
      }
    };

    const goToNextQuestion = () => {
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
    };

    const getAnswerStatus = (questionId: any) => {
      if (!isExamCompleted) return null;
      const question = questions.find((question: any) => question.id === questionId);
      const userAnswer = selectedAnswers[questionId];

      if (!userAnswer) return "empty";
      if (userAnswer === question.correctOption) return "correct";
      return "incorrect";
    };

    const getQuestionCardClass = (index: number) => {
      const baseClass = "p-1.5 rounded-md border-2 cursor-pointer transition-all text-sm";
      const isActive = index === currentQuestionIndex;

      if (!isExamCompleted) {
        const isAnswered = selectedAnswers[questions[index].id];
        if (isAnswered) {
          return `${baseClass} border-blue-400 bg-blue-50 ${isActive ? "border-blue-500 bg-blue-100" : ""}`;
        }
        return `${baseClass} ${isActive ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50"
          }`;
      }

      const status = getAnswerStatus(questions[index].id);
      if (status === "correct") {
        return `${baseClass} border-green-400 bg-green-50 ${isActive ? "border-green-500 bg-green-100" : ""}`;
      } else if (status === "incorrect") {
        return `${baseClass} border-red-400 bg-red-50 ${isActive ? "border-red-500 bg-red-100" : ""}`;
      } else {
        return `${baseClass} border-amber-400 bg-amber-50 ${isActive ? "border-amber-500 bg-amber-100" : ""}`;
      }
    };

    if (questions.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-8">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 bg-blue-100 rounded-full animate-pulse"></div>
            </div>
          </div>
          <p className="mt-6 text-lg font-semibold text-slate-700">Test yükleniyor...</p>
        </div>
      );
    }

    return (
      <>
        <div className={`w-full bg-gray-100 flex flex-col ${isFullscreen ? "h-screen overflow-y-auto" : "min-h-screen md:min-h-0"} relative`}>
          {isDrawingModeEnabled && isFullscreen && (
            <canvas
              ref={canvasRef}
              className="absolute inset-0 w-full h-full z-30 pointer-events-auto cursor-crosshair"
              style={{
                touchAction: "pan-y",
                ...(drawingTool === "eraser"
                  ? {
                    cursor: `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='${eraserSize}' height='${eraserSize}' viewBox='0 0 ${eraserSize} ${eraserSize}'><circle cx='${eraserSize / 2
                      }' cy='${eraserSize / 2}' r='${eraserSize / 2 - 1}' fill='none' stroke='%23ef4444' stroke-width='2'/></svg>") ${eraserSize / 2
                      } ${eraserSize / 2}, auto`,
                  }
                  : {}),
              }}
              onMouseDown={(e) => {
                const canvas = canvasRef.current;
                if (!canvas) return;

                const rect = canvas.getBoundingClientRect();
                const x = e.clientX - rect.left + window.scrollX;
                const y = e.clientY - rect.top + window.scrollY;

                if (isInRestrictedArea(e.clientX, e.clientY)) {
                  return;
                }

                e.preventDefault();
                setIsDrawing(true);

                const ctx = canvas.getContext("2d");
                if (ctx) {
                  ctx.beginPath();
                  ctx.moveTo(x, y);
                }
              }}
              onMouseMove={(e) => {
                if (!isDrawing) return;

                const canvas = canvasRef.current;
                if (!canvas) return;

                const rect = canvas.getBoundingClientRect();
                const x = e.clientX - rect.left + window.scrollX;
                const y = e.clientY - rect.top + window.scrollY;

                if (isInRestrictedArea(e.clientX, e.clientY)) {
                  setIsDrawing(false);
                  return;
                }

                e.preventDefault();

                const ctx = canvas.getContext("2d");
                if (ctx) {
                  ctx.lineTo(x, y);
                  if (drawingTool === "pen") {
                    ctx.globalCompositeOperation = "source-over";
                    ctx.strokeStyle = penColor;
                    ctx.lineWidth = penSize;
                  } else {
                    ctx.globalCompositeOperation = "destination-out";
                    ctx.lineWidth = eraserSize;
                  }
                  ctx.lineCap = "round";
                  ctx.lineJoin = "round";
                  ctx.stroke();
                }
              }}
              onMouseUp={() => setIsDrawing(false)}
              onMouseLeave={() => setIsDrawing(false)}
            />
          )}

          <div className="flex flex-1 flex-col md:flex-row">
            {isAnswerKeyVisible && (
              <div
                className={`${isSidebarCollapsed ? "w-12" : "w-full md:w-64 lg:w-56"
                  } bg-white border-r md:border-r border-b md:border-b-0 border-slate-200 flex flex-col drawing-restricted-left-panel transition-all duration-300 ${isDrawingModeEnabled && isFullscreen ? "relative z-50" : ""
                  }`}>
                <div className="p-2 border-b border-slate-200">
                  <button
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                    className="w-full flex items-center justify-center p-2 hover:bg-slate-50 rounded-md transition-colors"
                    title={isSidebarCollapsed ? "Paneli Aç" : "Paneli Kapat"}>
                    {isSidebarCollapsed ? <Menu className="w-5 h-5 text-slate-600" /> : <ChevronLeft className="w-5 h-5 text-slate-600" />}
                  </button>
                </div>

                {!isSidebarCollapsed && (
                  <>
                    <div className="p-2 md:p-4">
                      <div className="grid grid-cols-4 md:grid-cols-3 gap-1 md:gap-1.5">
                        {questions.map((question, index) => {
                          return (
                            <div key={question.id} className={getQuestionCardClass(index)} onClick={() => goToQuestion(index)}>
                              <div className="flex items-center justify-center">
                                <span className="font-medium text-slate-900 text-xs">{index + 1}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="border-b border-slate-200"></div>

                    <div className="flex-none p-2 md:p-3 md:flex-1 md:flex md:flex-col">
                      <div className="md:hidden">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <button
                            onClick={() => setIsDrawingPanelCollapsed(!isDrawingPanelCollapsed)}
                            className="flex items-center gap-1 text-xs font-semibold text-slate-600">
                            <span>Çizim</span>
                            {isDrawingPanelCollapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                          </button>
                          {!isDrawingPanelCollapsed && (
                            <button
                              onClick={() => setIsDrawingModeEnabled(!isDrawingModeEnabled)}
                              className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${isDrawingModeEnabled ? "bg-blue-600" : "bg-gray-200"
                                }`}>
                              <span
                                className={`inline-block h-2 w-2 transform rounded-full bg-white transition-transform ${isDrawingModeEnabled ? "translate-x-4" : "translate-x-1"
                                  }`}
                              />
                            </button>
                          )}
                        </div>

                        {!isDrawingPanelCollapsed && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setDrawingTool("pen")}
                              disabled={!isDrawingModeEnabled}
                              className={`p-1.5 rounded text-xs flex items-center justify-center transition-all ${!isDrawingModeEnabled
                                  ? "bg-slate-50 text-slate-400 cursor-not-allowed"
                                  : drawingTool === "pen"
                                    ? "bg-blue-600 text-white"
                                    : "bg-slate-100 text-slate-600"
                                }`}>
                              <Pen className="w-2.5 h-2.5" />
                            </button>
                            <button
                              onClick={() => setDrawingTool("eraser")}
                              disabled={!isDrawingModeEnabled}
                              className={`p-1.5 rounded text-xs flex items-center justify-center transition-all ${!isDrawingModeEnabled
                                  ? "bg-slate-50 text-slate-400 cursor-not-allowed"
                                  : drawingTool === "eraser"
                                    ? "bg-orange-600 text-white"
                                    : "bg-slate-100 text-slate-600"
                                }`}>
                              <Eraser className="w-2.5 h-2.5" />
                            </button>

                            {drawingTool === "pen" && (
                              <div className="flex gap-1 ml-1">
                                {colorOptions.slice(0, 3).map((color) => (
                                  <button
                                    key={color.value}
                                    onClick={() => setPenColor(color.value)}
                                    disabled={!isDrawingModeEnabled}
                                    className={`w-4 h-4 rounded-full border-2 transition-all ${!isDrawingModeEnabled
                                        ? "border-slate-200 opacity-50"
                                        : penColor === color.value
                                          ? "border-slate-600 scale-110"
                                          : "border-slate-300"
                                      }`}
                                    style={{ backgroundColor: color.value }}
                                  />
                                ))}
                              </div>
                            )}

                            <div className="flex items-center gap-1 ml-1">
                              <span className="text-xs text-slate-600 min-w-[20px]">{drawingTool === "pen" ? penSize : eraserSize}px</span>
                              <input
                                type="range"
                                min={drawingTool === "pen" ? 1 : 5}
                                max={drawingTool === "pen" ? 10 : 30}
                                value={drawingTool === "pen" ? penSize : eraserSize}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value);
                                  if (drawingTool === "pen") {
                                    setPenSize(value);
                                  } else {
                                    setEraserSize(value);
                                  }
                                }}
                                disabled={!isDrawingModeEnabled}
                                className={`w-12 h-1 bg-slate-200 rounded-lg appearance-none touch-manipulation ${isDrawingModeEnabled ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                                  }`}
                              />
                            </div>

                            <button
                              onClick={() => setShowWhiteboard(!showWhiteboard)}
                              disabled={!isDrawingModeEnabled}
                              className={`p-1.5 rounded text-xs flex items-center justify-center ml-auto transition-all ${!isDrawingModeEnabled
                                  ? "bg-slate-50 text-slate-400 cursor-not-allowed"
                                  : showWhiteboard
                                    ? "bg-purple-600 text-white"
                                    : "bg-purple-100 text-purple-600"
                                }`}>
                              <Palette className="w-2.5 h-2.5" />
                            </button>

                            <button
                              onClick={() => {
                                if (canvasRef.current) {
                                  const ctx = canvasRef.current.getContext("2d");
                                  if (ctx) {
                                    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                                  }
                                }
                                if (whiteboardRef.current) {
                                  const ctx = whiteboardRef.current.getContext("2d");
                                  if (ctx) {
                                    ctx.clearRect(0, 0, whiteboardRef.current.width, whiteboardRef.current.height);
                                  }
                                }
                              }}
                              disabled={!isDrawingModeEnabled}
                              className={`p-1.5 rounded text-xs flex items-center justify-center transition-all ${!isDrawingModeEnabled ? "bg-slate-50 text-slate-400 cursor-not-allowed" : "bg-orange-100 text-orange-600"
                                }`}>
                              <RotateCcw className="w-2.5 h-2.5" />
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="hidden md:block flex-1 space-y-2 md:space-y-3">
                        <div className="flex items-center justify-between mb-1 md:mb-2">
                          <h3 className="text-xs font-semibold text-slate-600">Çizim Araçları</h3>
                          <div className="flex items-center gap-1 md:gap-2">
                            <span className="text-xs text-slate-500 hidden sm:inline">{isDrawingModeEnabled ? "Açık" : "Kapalı"}</span>
                            <button
                              onClick={() => setIsDrawingModeEnabled(!isDrawingModeEnabled)}
                              className={`relative inline-flex h-4 w-7 md:h-5 md:w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isDrawingModeEnabled ? "bg-blue-600" : "bg-gray-200"
                                }`}>
                              <span
                                className={`inline-block h-2 w-2 md:h-3 md:w-3 transform rounded-full bg-white transition-transform ${isDrawingModeEnabled ? "translate-x-4 md:translate-x-5" : "translate-x-1"
                                  }`}
                              />
                            </button>
                          </div>
                        </div>

                        <div className="flex gap-0.5 md:gap-1">
                          <button
                            onClick={() => setDrawingTool("pen")}
                            disabled={!isDrawingModeEnabled}
                            className={`flex-1 p-1.5 md:p-2 rounded text-xs flex items-center justify-center gap-0.5 md:gap-1 transition-all ${!isDrawingModeEnabled
                                ? "bg-slate-50 text-slate-400 cursor-not-allowed"
                                : drawingTool === "pen"
                                  ? "bg-blue-600 text-white"
                                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                              }`}>
                            <Pen className="w-2.5 h-2.5 md:w-3 md:h-3" />
                            <span className="hidden sm:inline">Kalem</span>
                          </button>
                          <button
                            onClick={() => setDrawingTool("eraser")}
                            disabled={!isDrawingModeEnabled}
                            className={`flex-1 p-1.5 md:p-2 rounded text-xs flex items-center justify-center gap-0.5 md:gap-1 transition-all ${!isDrawingModeEnabled
                                ? "bg-slate-50 text-slate-400 cursor-not-allowed"
                                : drawingTool === "eraser"
                                  ? "bg-orange-600 text-white"
                                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                              }`}>
                            <Eraser className="w-2.5 h-2.5 md:w-3 md:h-3" />
                            <span className="hidden sm:inline">Silgi</span>
                          </button>
                        </div>

                        {drawingTool === "pen" && isDrawingModeEnabled && (
                          <div>
                            <label className="text-xs text-slate-600 block mb-1 md:mb-2">
                              <span className="hidden sm:inline">Kalem Rengi</span>
                              <span className="sm:hidden">Renk</span>
                            </label>
                            <div className="flex justify-center gap-1 md:gap-1.5 mb-2 md:mb-3">
                              {colorOptions.slice(0, 4).map((color) => (
                                <button
                                  key={color.value}
                                  onClick={() => setPenColor(color.value)}
                                  className={`w-4 h-4 md:w-5 md:h-5 rounded-full border-2 transition-all ${penColor === color.value
                                      ? "border-slate-600 ring-1 md:ring-2 ring-slate-300 scale-110"
                                      : "border-slate-300 hover:border-slate-400 hover:scale-105"
                                    }`}
                                  style={{ backgroundColor: color.value }}
                                  title={color.name}
                                />
                              ))}
                              <div className="hidden sm:flex gap-1.5">
                                {colorOptions.slice(4).map((color) => (
                                  <button
                                    key={color.value}
                                    onClick={() => setPenColor(color.value)}
                                    className={`w-5 h-5 rounded-full border-2 transition-all ${penColor === color.value
                                        ? "border-slate-600 ring-2 ring-slate-300 scale-110"
                                        : "border-slate-300 hover:border-slate-400 hover:scale-105"
                                      }`}
                                    style={{ backgroundColor: color.value }}
                                    title={color.name}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {isDrawingModeEnabled && (
                          <div>
                            <label className="text-xs text-slate-600 block mb-1">
                              <span className="hidden sm:inline">{drawingTool === "pen" ? "Kalem" : "Silgi"} Kalınlığı: </span>
                              <span className="sm:hidden">{drawingTool === "pen" ? "K" : "S"}: </span>
                              {drawingTool === "pen" ? penSize : eraserSize}px
                            </label>
                            <input
                              type="range"
                              min={drawingTool === "pen" ? 1 : 5}
                              max={drawingTool === "pen" ? 10 : 30}
                              value={drawingTool === "pen" ? penSize : eraserSize}
                              onChange={(e) => {
                                const value = parseInt(e.target.value);
                                if (drawingTool === "pen") {
                                  setPenSize(value);
                                } else {
                                  setEraserSize(value);
                                }
                              }}
                              className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer touch-manipulation"
                            />
                          </div>
                        )}

                        <button
                          onClick={() => setShowWhiteboard(!showWhiteboard)}
                          className={`w-full p-1.5 md:p-1.5 rounded text-xs flex items-center justify-center gap-0.5 md:gap-1 transition-all ${showWhiteboard ? "bg-purple-600 text-white" : "bg-purple-100 text-purple-600 hover:bg-purple-200"
                            }`}>
                          <Palette className="w-2.5 h-2.5 md:w-3 md:h-3" />
                          <span className="hidden sm:inline">{showWhiteboard ? "Kapat" : "Tahta"}</span>
                          <span className="sm:hidden">{showWhiteboard ? "×" : "T"}</span>
                        </button>

                        {isDrawingModeEnabled && (
                          <button
                            onClick={() => {
                              if (canvasRef.current) {
                                const ctx = canvasRef.current.getContext("2d");
                                if (ctx) {
                                  ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                                }
                              }
                              if (whiteboardRef.current) {
                                const ctx = whiteboardRef.current.getContext("2d");
                                if (ctx) {
                                  ctx.clearRect(0, 0, whiteboardRef.current.width, whiteboardRef.current.height);
                                }
                              }
                            }}
                            className="w-full p-1.5 md:p-1.5 rounded text-xs flex items-center justify-center gap-0.5 md:gap-1 bg-orange-100 text-orange-600 hover:bg-orange-200 transition-all">
                            <RotateCcw className="w-2.5 h-2.5 md:w-3 md:h-3" />
                            <span className="hidden sm:inline">Temizle</span>
                            <span className="sm:hidden">↻</span>
                          </button>
                        )}

                        <div className="mt-3 space-y-3">
                          {!isExamCompleted && (
                            <button
                              className="w-full p-2 rounded-lg text-sm cursor-pointer transition-all bg-indigo-600 text-white hover:bg-indigo-700"
                              onClick={handleExamComplete}>
                              Sınavı Kaydet
                            </button>
                          )}

                          {isExamCompleted && (
                            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3 shadow-sm">
                              <div className="text-center">
                                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center justify-center gap-2">
                                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                                  Sınav Sonucu
                                </h4>

                                <div className="mb-4">
                                  {(() => {
                                    const { totalQuestions, correctAnswers, wrongAnswers, unanswered, successRate } = calculateExamStats();

                                    if (!isExamCompleted) {
                                      return null;
                                    }

                                    return (
                                      <>
                                        <div className="relative mb-4">
                                          <div className="w-20 h-20 mx-auto rounded-full bg-white flex items-center justify-center">
                                            <svg className="w-20 h-20 absolute transform -rotate-90" viewBox="0 0 100 100">
                                              <circle cx="50" cy="50" r="40" stroke="#e5e7eb" strokeWidth="8" fill="white" />
                                              <circle
                                                cx="50"
                                                cy="50"
                                                r="40"
                                                stroke={successRate >= 80 ? "#10b981" : successRate >= 60 ? "#f59e0b" : "#ef4444"}
                                                strokeWidth="8"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeDasharray={`${2 * Math.PI * 40}`}
                                                strokeDashoffset={`${2 * Math.PI * 40 * (1 - successRate / 100)}`}
                                                className="transition-all duration-1000 ease-out"
                                              />
                                            </svg>
                                            <div className="relative z-10 text-center">
                                              <div
                                                className={`text-sm font-bold ${successRate >= 80
                                                    ? "text-green-600"
                                                    : successRate >= 60
                                                      ? "text-yellow-600"
                                                      : "text-red-600"
                                                  }`}>
                                                %{successRate}
                                              </div>
                                              <div className="text-xs text-gray-500 -mt-0.5">başarı</div>
                                            </div>
                                          </div>
                                        </div>

                                        <div className="space-y-2">
                                          <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-2">
                                            <div className="flex items-center gap-2">
                                              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                                                <CheckCircle2 className="w-3 h-3 text-green-600" />
                                              </div>
                                              <span className="text-xs font-medium text-green-700">Doğru</span>
                                            </div>
                                            <div className="text-sm font-bold text-green-800">{correctAnswers}</div>
                                          </div>

                                          <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg p-2">
                                            <div className="flex items-center gap-2">
                                              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                                                <X className="w-3 h-3 text-red-600" />
                                              </div>
                                              <span className="text-xs font-medium text-red-700">Yanlış</span>
                                            </div>
                                            <div className="text-sm font-bold text-red-800">{wrongAnswers}</div>
                                          </div>

                                          <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-2">
                                            <div className="flex items-center gap-2">
                                              <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                                                <Circle className="w-3 h-3 text-gray-500" />
                                              </div>
                                              <span className="text-xs font-medium text-gray-700">Boş</span>
                                            </div>
                                            <div className="text-sm font-bold text-gray-800">{unanswered}</div>
                                          </div>

                                          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-2 mt-2">
                                            <div className="flex items-center gap-2">
                                              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                                                <ClipboardCheck className="w-3 h-3 text-blue-600" />
                                              </div>
                                              <span className="text-xs font-medium text-blue-700">Toplam</span>
                                            </div>
                                            <div className="text-sm font-bold text-blue-800">{totalQuestions}</div>
                                          </div>
                                        </div>

                                        <div className="mt-4 text-center">
                                          <div
                                            className={`text-xs font-medium px-3 py-2 rounded-full inline-block shadow-sm ${successRate >= 80
                                                ? "bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200"
                                                : successRate >= 60
                                                  ? "bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-700 border border-yellow-200"
                                                  : "bg-gradient-to-r from-red-100 to-pink-100 text-red-700 border border-red-200"
                                              }`}>
                                            {successRate >= 80
                                              ? "🎉 Mükemmel Performans!"
                                              : successRate >= 60
                                                ? "👍 İyi Çalışma!"
                                                : successRate >= 40
                                                  ? "📚 Daha İyi Olabilir!"
                                                  : "💪 Tekrar Deneyelim!"}
                                          </div>
                                          <div className="text-xs text-gray-500 mt-2">
                                            {successRate >= 80
                                              ? "Tebrikler! Harika bir sonuç."
                                              : successRate >= 60
                                                ? "Güzel! Biraz daha çalışarak daha da iyisini yapabilirsin."
                                                : successRate >= 40
                                                  ? "Fena değil! Biraz daha odaklanarak gelişebilirsin."
                                                  : "Sorun değil! Tekrar deneyerek daha iyi sonuçlar alabilirsin."}
                                          </div>
                                        </div>
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className={`min-w-0 flex-1 ${isFullscreen ? "overflow-y-auto" : "overflow-y-auto"}`} style={{ touchAction: "pan-y pinch-zoom" }}>
              <div className={`p-4 sm:p-6 ${isFullscreen && questionAiPanelOpen ? "md:p-5" : "md:p-8"} ${isFullscreen ? (isMobilePhone() ? "pb-32" : "pb-28") : ""}`}>
                <div className="w-full mx-auto transition-all duration-300">
                  <div className={`bg-white rounded-lg border border-slate-200 shadow-sm p-4 sm:p-6 ${isFullscreen && questionAiPanelOpen ? "mb-3 sm:mb-4" : "mb-4 sm:mb-6"} relative`}>
                    {isDrawingModeEnabled && !isFullscreen && (
                      <canvas
                        ref={canvasRef}
                        className={`absolute top-0 left-0 w-full h-full z-10 rounded-lg pointer-events-auto touch-none ${drawingTool === "eraser" ? "cursor-none" : "cursor-crosshair"
                          }`}
                        style={
                          drawingTool === "eraser"
                            ? {
                              cursor: `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='${eraserSize}' height='${eraserSize}' viewBox='0 0 ${eraserSize} ${eraserSize}'><circle cx='${eraserSize / 2
                                }' cy='${eraserSize / 2}' r='${eraserSize / 2 - 1
                                }' fill='none' stroke='%23ef4444' stroke-width='2'/></svg>") ${eraserSize / 2} ${eraserSize / 2}, auto`,
                            }
                            : {}
                        }
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setIsDrawing(true);
                          const canvas = canvasRef.current;
                          if (!canvas) return;

                          const rect = canvas.getBoundingClientRect();
                          const scaleX = canvas.width / (rect.width * window.devicePixelRatio);
                          const scaleY = canvas.height / (rect.height * window.devicePixelRatio);
                          const x = (e.clientX - rect.left) / scaleX;
                          const y = (e.clientY - rect.top) / scaleY;

                          const ctx = canvas.getContext("2d");
                          if (ctx) {
                            ctx.beginPath();
                            ctx.moveTo(x, y);
                          }
                        }}
                        onMouseMove={(e) => {
                          if (!isDrawing) return;
                          e.preventDefault();
                          const canvas = canvasRef.current;
                          if (!canvas) return;

                          const rect = canvas.getBoundingClientRect();
                          const scaleX = canvas.width / (rect.width * window.devicePixelRatio);
                          const scaleY = canvas.height / (rect.height * window.devicePixelRatio);
                          const x = (e.clientX - rect.left) / scaleX;
                          const y = (e.clientY - rect.top) / scaleY;
                          const ctx = canvas.getContext("2d");
                          if (ctx) {
                            ctx.lineTo(x, y);
                            if (drawingTool === "pen") {
                              ctx.globalCompositeOperation = "source-over";
                              ctx.strokeStyle = penColor;
                              ctx.lineWidth = penSize;
                            } else {
                              ctx.globalCompositeOperation = "destination-out";
                              ctx.lineWidth = eraserSize;
                            }
                            ctx.lineCap = "round";
                            ctx.lineJoin = "round";
                            ctx.stroke();
                          }
                        }}
                        onMouseUp={() => setIsDrawing(false)}
                        onMouseLeave={() => setIsDrawing(false)}
                      />
                    )}

                    {currentQuestion.media && (
                      <div
                        ref={imageContainerRef}
                        className={`${isFullscreen && questionAiPanelOpen ? "mb-3" : "mb-6"} w-full flex justify-center items-center overflow-hidden`}
                        style={{
                          height: getImageHeight(),
                          touchAction: imageZoom > 1 ? "none" : "auto",
                        }}>
                        <div
                          className="relative"
                          style={{
                            transform: `translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                            transition: imageZoom === 1 ? "transform 0.3s ease" : "none",
                          }}>
                          <img
                            ref={imageRef}
                            src={currentQuestion.media.url}
                            alt="Soru görseli"
                            className="max-h-full object-contain transition-transform duration-200 select-none"
                            style={{
                              transform: `scale(${imageZoom})`,
                              cursor: isDrawingModeEnabled ? "default" : imageZoom > 1 ? (isDragging ? "grabbing" : "grab") : "zoom-in",
                              maxHeight: getImageHeight(),
                              width: "auto",
                              touchAction: imageZoom > 1 ? "none" : "manipulation",
                            }}
                            onClick={handleImageClick}
                            onMouseDown={handleImageMouseDown}
                            onMouseMove={handleImageMouseMove}
                            onMouseUp={handleImageMouseUp}
                            onMouseLeave={handleImageMouseUp}
                            onContextMenu={(e) => e.preventDefault()}
                            draggable={false}
                          />
                          {imageZoom > 1 && !isDrawingModeEnabled && (
                            <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm">
                              {imageZoom.toFixed(1)}x
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {!currentQuestion.media && currentQuestion?.metadata?.imageUrl && (
                      <div
                        ref={imageContainerRef}
                        className={`${isFullscreen && questionAiPanelOpen ? "mb-3" : "mb-6"} w-full flex justify-center items-center overflow-hidden`}
                        style={{
                          height: getImageHeight(),
                          touchAction: imageZoom > 1 ? "none" : "auto",
                        }}>
                        <div
                          className="relative"
                          style={{
                            transform: `translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                            transition: imageZoom === 1 ? "transform 0.3s ease" : "none",
                          }}>
                          <img
                            ref={imageRef}
                            src={currentQuestion.metadata.imageUrl}
                            alt="Soru görseli"
                            className="max-h-full object-contain transition-transform duration-200 select-none"
                            style={{
                              transform: `scale(${imageZoom})`,
                              cursor: isDrawingModeEnabled ? "default" : imageZoom > 1 ? (isDragging ? "grabbing" : "grab") : "zoom-in",
                              maxHeight: getImageHeight(),
                              width: "auto",
                              touchAction: imageZoom > 1 ? "none" : "manipulation",
                            }}
                            onClick={handleImageClick}
                            onMouseDown={handleImageMouseDown}
                            onMouseMove={handleImageMouseMove}
                            onMouseUp={handleImageMouseUp}
                            onMouseLeave={handleImageMouseUp}
                            onContextMenu={(e) => e.preventDefault()}
                            draggable={false}
                          />
                        </div>
                      </div>
                    )}

                    {!currentQuestion.media && !currentQuestion?.metadata?.imageUrl && currentQuestion?.metadata?.imageDataUrl && (
                      <div
                        ref={imageContainerRef}
                        className={`${isFullscreen && questionAiPanelOpen ? "mb-3" : "mb-6"} w-full flex justify-center items-center overflow-hidden`}
                        style={{
                          height: getImageHeight(),
                          touchAction: imageZoom > 1 ? "none" : "auto",
                        }}>
                        <div
                          className="relative"
                          style={{
                            transform: `translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                            transition: imageZoom === 1 ? "transform 0.3s ease" : "none",
                          }}>
                          <img
                            ref={imageRef}
                            src={currentQuestion.metadata.imageDataUrl}
                            alt="Soru görseli"
                            className="max-h-full object-contain transition-transform duration-200 select-none"
                            style={{
                              transform: `scale(${imageZoom})`,
                              cursor: isDrawingModeEnabled ? "default" : imageZoom > 1 ? (isDragging ? "grabbing" : "grab") : "zoom-in",
                              maxHeight: getImageHeight(),
                              width: "auto",
                              touchAction: imageZoom > 1 ? "none" : "manipulation",
                            }}
                            onClick={handleImageClick}
                            onMouseDown={handleImageMouseDown}
                            onMouseMove={handleImageMouseMove}
                            onMouseUp={handleImageMouseUp}
                            onMouseLeave={handleImageMouseUp}
                            onContextMenu={(e) => e.preventDefault()}
                            draggable={false}
                          />
                        </div>
                      </div>
                    )}

                    {currentQuestion.question && (
                      <div className={`${isFullscreen && questionAiPanelOpen ? "mb-4" : "mb-6"} bg-gray-50 rounded-lg border ${isFullscreen && questionAiPanelOpen ? "p-4" : isFullscreen ? "p-6" : "p-4"}`}>
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
                          <h3 className={`font-semibold text-gray-900 ${isFullscreen ? "text-2xl" : "text-lg"}`}>
                            Soru {currentQuestionIndex + 1}
                          </h3>
                          {isCurrentQuestionFromQuestionBank && (
                            <button
                              type="button"
                              onClick={() => void handleQuestionAiButtonClick()}
                              className={`inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                                questionAiPanelOpen
                                  ? "border-blue-300 bg-blue-50 text-blue-700"
                                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                              }`}>
                              <Sparkles className="w-4 h-4" />
                              AI Yardım
                            </button>
                          )}
                        </div>
                        <p className={`text-gray-800 leading-relaxed ${isFullscreen && !questionAiPanelOpen ? "text-xl" : ""}`}>{currentQuestion.question}</p>
                      </div>
                    )}

                    {!currentQuestion.question && isCurrentQuestionFromQuestionBank && (
                      <div className="mb-4 flex justify-end">
                        <button
                          type="button"
                          onClick={() => void handleQuestionAiButtonClick()}
                          className={`inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                            questionAiPanelOpen
                              ? "border-blue-300 bg-blue-50 text-blue-700"
                              : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                          }`}>
                          <Sparkles className="w-4 h-4" />
                          AI Yardım
                        </button>
                      </div>
                    )}

                    <div
                      className={`space-y-3 relative drawing-restricted-buttons ${isDrawingModeEnabled && isFullscreen ? "z-50" : "z-20"}`}>
                      <div className="grid grid-cols-5 gap-1 sm:gap-2 max-w-5xl mx-auto">
                        {currentQuestion.options.map((option: string, optionIndex: number) => (
                          <button
                            key={option}
                            onClick={() => handleAnswerSelect(currentQuestion.id, option)}
                            disabled={isExamCompleted}
                            className={`w-full px-1 py-1 sm:p-4 text-center text-xs sm:text-base rounded border-2 transition-all hover:shadow-md relative z-30 ${selectedAnswers[currentQuestion.id] === option
                                ? !isExamCompleted
                                  ? "border-indigo-500 bg-indigo-50 text-indigo-800"
                                  : selectedAnswers[currentQuestion.id] === currentQuestion.correctOption
                                    ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                                    : "border-rose-500 bg-rose-50 text-rose-800"
                                : isExamCompleted && option === currentQuestion.correctOption
                                  ? "border-emerald-400 bg-emerald-25 text-emerald-700"
                                  : "border-slate-200 bg-white hover:border-slate-300 text-slate-800"
                              } ${isExamCompleted ? "cursor-not-allowed" : "cursor-pointer"}`}>
                            <div className="flex items-center">
                              <span className="flex-1 text-center text-xs sm:text-base">{option}</span>
                              {selectedAnswers[currentQuestion.id] === option && (
                                <CheckCircle2
                                  className={`w-3 h-3 sm:w-5 sm:h-5 mt-0 sm:mt-1 ${!isExamCompleted
                                      ? "text-indigo-500"
                                      : selectedAnswers[currentQuestion.id] === currentQuestion.correctOption
                                        ? "text-emerald-500"
                                        : "text-rose-500"
                                    }`}
                                />
                              )}
                              {isExamCompleted &&
                                option === currentQuestion.correctOption &&
                                selectedAnswers[currentQuestion.id] !== option && (
                                  <CheckCircle2 className="w-3 h-3 sm:w-5 sm:h-5 mt-0 sm:mt-1 text-emerald-500" />
                                )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {isExamCompleted && (
                      <div className="mt-6 p-4 rounded-lg border border-slate-200 bg-slate-50">
                        <h4 className="font-semibold text-slate-900 mb-3">Cevap Detayları</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600">Doğru Cevap:</span>
                            <span className="font-medium text-emerald-600">{currentQuestion.correctOption}</span>
                          </div>
                          {selectedAnswers[currentQuestion.id] && (
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">Sizin Cevabınız:</span>
                              <span
                                className={`font-medium ${selectedAnswers[currentQuestion.id] === currentQuestion.correctOption ? "text-emerald-600" : "text-rose-600"
                                  }`}>
                                {selectedAnswers[currentQuestion.id]}
                              </span>
                            </div>
                          )}
                          {!selectedAnswers[currentQuestion.id] && (
                            <div className="flex items-center justify-between">
                              <span className="text-slate-600">Sizin Cevabınız:</span>
                              <span className="font-medium text-amber-600">Cevaplanmamış</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div
                    className={`flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0 drawing-restricted-nav ${isDrawingModeEnabled && isFullscreen ? "z-50" : ""
                      } ${isFullscreen && isMobilePhone()
                        ? "fixed bottom-0 left-0 right-0 p-3 z-50"
                        : isFullscreen
                          ? fullscreenDesktopNavClass
                          : ""
                      }`}>
                    <button
                      onClick={goToPreviousQuestion}
                      disabled={currentQuestionIndex === 0}
                      className={`flex items-center gap-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto justify-center ${isFullscreen && isMobilePhone() ? "px-2 py-1" : "px-4 py-2 sm:px-6 sm:py-3"
                        }`}>
                      <ChevronLeft className={`${isFullscreen && isMobilePhone() ? "w-3 h-3" : "w-4 h-4 sm:w-5 sm:h-5"}`} />
                      <span className={`${isFullscreen && isMobilePhone() ? "text-xs" : "text-sm sm:text-base"}`}>
                        {isFullscreen && isMobilePhone() ? "Önceki" : "Önceki Soru"}
                      </span>
                    </button>

                    <div className={`text-slate-500 order-first sm:order-none ${isFullscreen && isMobilePhone() ? "text-xs" : "text-sm"}`}>
                      {currentQuestionIndex + 1} / {questions.length}
                    </div>

                    {currentQuestionIndex === questions.length - 1 ? (
                      !isExamCompleted ? (
                        <button
                          onClick={handleExamComplete}
                          disabled={isExamMutationPending}
                          className={`flex items-center gap-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors w-full sm:w-auto justify-center ${isFullscreen && isMobilePhone() ? "px-2 py-1" : "px-4 py-2 sm:px-6 sm:py-3"
                            } disabled:opacity-60 disabled:cursor-not-allowed`}>
                          <span className={`${isFullscreen && isMobilePhone() ? "text-xs" : "text-sm sm:text-base"}`}>
                            {isFullscreen && isMobilePhone() ? "Kaydet" : "Sınavı Kaydet"}
                          </span>
                          <CheckCircle2 className={`${isFullscreen && isMobilePhone() ? "w-3 h-3" : "w-4 h-4 sm:w-5 sm:h-5"}`} />
                        </button>
                      ) : null
                    ) : (
                      <button
                        onClick={goToNextQuestion}
                        className={`flex items-center gap-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors w-full sm:w-auto justify-center ${isFullscreen && isMobilePhone() ? "px-2 py-1" : "px-4 py-2 sm:px-6 sm:py-3"
                          }`}>
                        <span className={`${isFullscreen && isMobilePhone() ? "text-xs" : "text-sm sm:text-base"}`}>
                          {isFullscreen && isMobilePhone() ? "Sonraki" : "Sonraki Soru"}
                        </span>
                        <ChevronRight className={`${isFullscreen && isMobilePhone() ? "w-3 h-3" : "w-4 h-4 sm:w-5 sm:h-5"}`} />
                      </button>
                    )}

                    {isExamCompleted && isMobilePhone() && (
                      <div className="mt-3 flex justify-center w-full">
                        <button
                          onClick={() => setIsResultsModalOpen(true)}
                          className={`flex items-center gap-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors w-full justify-center ${isFullscreen && isMobilePhone() ? "px-2 py-1" : "px-4 py-2 sm:px-6 sm:py-3"
                            }`}>
                          <CheckCircle2 className={`${isFullscreen && isMobilePhone() ? "w-3 h-3" : "w-4 h-4 sm:w-5 sm:h-5"}`} />
                          <span className={`${isFullscreen && isMobilePhone() ? "text-xs" : "text-sm sm:text-base"}`}>
                            {isFullscreen && isMobilePhone() ? "Sonuçlar" : "Sonuçları Görün"}
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {renderQuestionAiPanel()}
          </div>

        </div>

        <Modal
          isOpen={isExamActionModalOpen}
          onClose={() => {
            if (!isExamMutationPending) setIsExamActionModalOpen(false);
          }}
          title="Sınav işlemini seç"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Cevaplarınızı kaydedip çıkabilir veya kaydedip sınavı tamamen bitirebilirsiniz.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  void handleSaveAndExit();
                }}
                disabled={isExamMutationPending}
                className="w-full rounded-[10px] border-[1.5px] border-[#f3c2d8] bg-[#fff7fb] px-[18px] py-[10px] text-[12px] font-bold uppercase tracking-[0.8px] text-[#c02d72] transition-all hover:bg-[#fff1f7] hover:border-[#e5a8c6] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isExamMutationPending ? "Kaydediliyor..." : "Kaydet ve Çık"}
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleSaveAndFinish();
                }}
                disabled={isExamMutationPending}
                className="w-full rounded-[10px] border-0 bg-[linear-gradient(135deg,#d63384,#b5246a)] px-[28px] py-[10px] text-[13px] font-bold uppercase tracking-[1px] text-white shadow-[0_3px_12px_rgba(214,51,132,0.25)] transition-all hover:-translate-y-px hover:shadow-[0_5px_20px_rgba(214,51,132,0.35)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-[0_3px_12px_rgba(214,51,132,0.25)]"
              >
                {isExamMutationPending ? "Bitiriliyor..." : "Kaydet ve Bitir"}
              </button>
            </div>
          </div>
        </Modal>
      </>
    );
  };

  const renderVideo = () => {
    const sourceUrl = content.media?.url || content.content || "";
    const cleaned = typeof sourceUrl === "string" ? sourceUrl.trim().replace(/^@+/, "") : "";

    const youtubeMatch =
      cleaned.match(/[?&]v=([A-Za-z0-9_-]{11})/) ||
      cleaned.match(/youtu\.be\/([A-Za-z0-9_-]{11})/) ||
      cleaned.match(/youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]{11})/);
    if (youtubeMatch) {
      const youtubeId = youtubeMatch[1];
      return (
        <div className="w-full bg-black">
          <div ref={videoContainerRef} className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${youtubeId}`}
              className="absolute top-0 left-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={`youtube-video-${youtubeId}`}
            />
          </div>
        </div>
      );
    }

    const match = cleaned.match(/(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/);

    if (match) {
      const videoId = match[1];
      return (
        <div className="w-full bg-black">
          <div ref={videoContainerRef} className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            <iframe
              src={`https://player.vimeo.com/video/${videoId}?autoplay=1&app_id=122963`}
              className="absolute top-0 left-0 w-full h-full"
              allow="autoplay; fullscreen"
              title={`vimeo-video-${videoId}`}
            />
          </div>
        </div>
      );
    }

    const videoSrc = cleaned;
    const posterUrl = content.media?.thumbnail || content.thumbnail || "";

    return (
      <div className="w-full bg-black">
        <div ref={videoContainerRef} className="w-full">
          <CustomVideoPlayer src={videoSrc} poster={posterUrl} />
        </div>
      </div>
    );
  };

  const renderText = () => {
    const textContent = content?.content || "";
    return (
      <div className="w-full bg-white">
        <div className="max-w-4xl mx-auto p-4 sm:p-6">
          <div className="whitespace-pre-wrap break-words text-slate-800 text-sm sm:text-base leading-relaxed">{textContent}</div>
        </div>
      </div>
    );
  };

  const renderCode = () => {
    const codeContent = content?.content || "";
    return (
      <div className="w-full bg-slate-900">
        <div className="max-w-4xl mx-auto p-4 sm:p-6">
          <pre className="text-xs sm:text-sm leading-relaxed overflow-auto rounded-lg bg-slate-900 text-slate-100 p-4">{codeContent}</pre>
        </div>
      </div>
    );
  };

  const renderTitleIcon = () => {
    const type = (content.type || "").toUpperCase();
    let iconBg = "bg-slate-50 text-slate-600";
    let iconEl = <FileText className="w-5 h-5" />;

    switch (type) {
      case "VIDEO":
        iconBg = "bg-purple-50 text-purple-600";
        iconEl = <Video className="w-5 h-5" />;
        break;
      case "AUDIO":
        iconBg = "bg-blue-50 text-blue-600";
        iconEl = <Volume2 className="w-5 h-5" />;
        break;
      case "IMAGE":
        iconBg = "bg-green-50 text-green-600";
        iconEl = <ImageIcon className="w-5 h-5" />;
        break;
      case "TEXT":
        iconBg = "bg-gray-50 text-gray-600";
        iconEl = <FileText className="w-5 h-5" />;
        break;
      case "FILE":
      case "PDF":
        iconBg = "bg-red-50 text-red-600";
        iconEl = <FileText className="w-5 h-5" />;
        break;
      case "CODE":
        iconBg = "bg-yellow-50 text-yellow-600";
        iconEl = <Code className="w-5 h-5" />;
        break;
      case "TEST":
      case "EXAM":
        iconBg = "bg-emerald-50 text-emerald-600";
        iconEl = <ClipboardCheck className="w-5 h-5" />;
        break;
      default:
        iconBg = "bg-slate-50 text-slate-600";
        iconEl = <FileText className="w-5 h-5" />;
    }

    return <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg}`}>{iconEl}</div>;
  };

  const renderTitle = () => {
    const type = (content.type || "").toUpperCase();
    switch (type) {
      case "VIDEO":
        return "Video";
      case "AUDIO":
        return "Ses";
      case "IMAGE":
        return "Resim";
      case "TEXT":
        return "Metin";
      case "FILE":
        return "Dosya";
      case "PDF":
        return "PDF";
      case "CODE":
        return "Kod";
      case "TEST":
      case "EXAM":
        return "Test";
      default:
        return "İçerik";
    }
  };

  const renderContent = () => {
    switch (content.type) {
      case "FILE":
        return renderFile();
      case "EXAM":
        return renderExam();
      case "TEST":
        return renderExam();
      case "VIDEO":
        return renderVideo();
      case "TEXT":
        return renderText();
      case "CODE":
        return renderCode();
      default:
        return renderFile();
    }
  };

  const exitFullscreen = () => {
    if (isFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);

      if (content.type === "FILE" || content.type === "PDF") {
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport && isMobilePhone()) {
          viewport.setAttribute("content", "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no");
        }
      }
    }
  };

  const handleExpand = () => {
    const contentViewer = document.getElementById("content-viewer");
    if (contentViewer) {
      if (isFullscreen) {
        exitFullscreen();
      } else {
        if (content.type === "FILE" || content.type === "PDF") {
          const viewport = document.querySelector('meta[name="viewport"]');
          if (viewport && isMobilePhone()) {
            viewport.setAttribute("content", "width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes");
          }
        }

        contentViewer.requestFullscreen();
        setIsFullscreen(true);

        setTimeout(() => {
          if (content.type === "EXAM" || content.type === "TEST") {
            let targetElement = document.querySelector(".mb-6.p-4.bg-gray-50.rounded-lg.border");

            if (!targetElement) {
              targetElement = document.querySelector(".drawing-restricted-buttons");
            }

            if (!targetElement) {
              targetElement = document.querySelector(".bg-white.rounded-lg.border.border-slate-200");
            }

            if (targetElement) {
              targetElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "nearest",
              });
            }
          } else if (content.type === "FILE" || content.type === "PDF") {
            const iframe = document.querySelector('iframe[title*="content-file"]') as HTMLIFrameElement;
            if (iframe && isMobilePhone()) {
              try {
                iframe.focus();
              } catch (e) { }

              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 200);
            }
          }
        }, 500);
      }
    }
  };

  const handleCopyContent = async () => {
    try {
      await navigator.clipboard.writeText(String(content?.content || ""));
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1500);
    } catch (_) { }
  };

  const isIOSDevice = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  };

  const handleDownloadFile = async () => {
    const fileUrl = content.media?.url || "";
    if (!fileUrl) return;

    const fileName = `${content.title || "dosya"}.pdf`;

    if (isIOSDevice()) {
      try {
        const response = await fetch(fileUrl);
        const blob = await response.blob();

        if (navigator.share) {
          await navigator.share({
            files: [new File([blob], fileName, { type: blob.type })],
            title: fileName,
          });
        } else {
          const url = window.URL.createObjectURL(blob);
          const newWindow = window.open(url, "_blank");
          if (newWindow) {
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 1000);
          } else {
            window.location.href = url;
          }
        }
      } catch (error) {
        console.error("iOS indirme hatası:", error);
        window.open(fileUrl, "_blank");
      }
    } else {
      try {
        const response = await fetch(fileUrl);
        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Dosya indirme hatası:", error);

        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = fileName;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  return (
    <div
      className={`bg-white w-full h-full ${isFullscreen && (content.type === "FILE" || content.type === "PDF") ? "overflow-hidden" : ""}`}
      id="content-viewer"
      style={{
        ...(isFullscreen &&
          (content.type === "FILE" || content.type === "PDF") && {
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 9999,
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
          userSelect: "none",
          WebkitTouchCallout: "none",
          WebkitTapHighlightColor: "transparent",
        }),
      }}>
      <div
        className={`w-full h-full bg-gray-100 flex flex-col ${isFullscreen && (content.type === "FILE" || content.type === "PDF") ? "h-screen" : ""
          }`}>
        <div
          className={`bg-white border-b border-gray-200 p-4 drawing-restricted-header ${isDrawingModeEnabled && isFullscreen ? "relative z-50" : ""
            } ${isFullscreen && (content.type === "FILE" || content.type === "PDF") ? "flex-shrink-0" : ""}`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center space-x-3">
              {renderTitleIcon()}
              <div>
                <h3 className="font-semibold text-gray-900">{content.title}</h3>
                <p className="text-sm text-gray-500">{renderTitle()}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              {["TEXT", "CODE"].includes(String(content?.type).toUpperCase()) && (
                <button
                  className={`flex items-center space-x-2 bg-white px-4 py-2 rounded-md border border-gray-300 text-sm font-medium hover:bg-gray-50 rounded-lg ${isCopied ? "text-green-700 border-green-300 bg-green-50" : "text-gray-700"
                    }`}
                  onClick={handleCopyContent}
                  title="İçeriği Kopyala">
                  {isCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span>{isCopied ? "Kopyalandı" : "Kopyala"}</span>
                </button>
              )}
              {["FILE", "PDF"].includes(String(content?.type).toUpperCase()) && content.media?.url && (
                <button
                  className="flex items-center space-x-2 bg-white px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                  onClick={handleDownloadFile}
                  title="Dosyayı İndir">
                  <Download className="w-4 h-4" />
                  <span>Dosyayı İndir</span>
                </button>
              )}
              {(content.type === "EXAM" || content.type === "TEST") &&
                isExamCompleted &&
                (questions[currentQuestionIndex]?.solutionMedia?.url || questions[currentQuestionIndex]?.metadata?.solutionVideoUrl) && (
                  <button
                    className="flex items-center space-x-2 bg-green-600 px-4 py-2 rounded-md text-sm font-medium text-white hover:bg-green-700 rounded-lg"
                    onClick={() => handleSolutionVideoToggle(questions[currentQuestionIndex])}
                    title="Çözüm Videosunu İzle">
                    <Play className="w-4 h-4" />
                    <span>Çözüm Videosu</span>
                  </button>
                )}
              {String(content?.type).toUpperCase() !== "VIDEO" && (
                <button
                  className="flex items-center space-x-2 bg-white px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                  onClick={handleExpand}>
                  <Expand className="w-4 h-4" />
                  <span>Tam Ekran</span>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className={`flex-1 ${isFullscreen && (content.type === "FILE" || content.type === "PDF") ? "h-full overflow-hidden" : ""}`}>
          {renderContent()}
        </div>

        {showWhiteboard && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col">
              <div className="p-3 sm:p-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Beyaz Tahta</h3>
                <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                  <div className="flex items-center gap-1 sm:gap-2 mr-2 sm:mr-4">
                    <button
                      onClick={() => setDrawingTool("pen")}
                      className={`p-1.5 sm:p-2 rounded transition-all ${drawingTool === "pen" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}>
                      <Pen className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                    <button
                      onClick={() => setDrawingTool("eraser")}
                      className={`p-1.5 sm:p-2 rounded transition-all ${drawingTool === "eraser" ? "bg-orange-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}>
                      <Eraser className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                    <input
                      type="range"
                      min={drawingTool === "pen" ? 1 : 5}
                      max={drawingTool === "pen" ? 10 : 30}
                      value={drawingTool === "pen" ? penSize : eraserSize}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (drawingTool === "pen") {
                          setPenSize(value);
                        } else {
                          setEraserSize(value);
                        }
                      }}
                      className="w-12 sm:w-20 h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer touch-manipulation"
                    />
                    <span className="text-xs text-slate-600 min-w-[25px] sm:min-w-[30px]">
                      {drawingTool === "pen" ? penSize : eraserSize}px
                    </span>

                    {drawingTool === "pen" && (
                      <div className="flex gap-1 sm:gap-1.5 ml-1 sm:ml-2">
                        {colorOptions.slice(0, 4).map((color) => (
                          <button
                            key={color.value}
                            onClick={() => setPenColor(color.value)}
                            className={`w-4 h-4 sm:w-6 sm:h-6 rounded-full border-2 transition-all ${penColor === color.value
                                ? "border-white ring-2 ring-gray-300 scale-110"
                                : "border-gray-300 hover:border-gray-400 hover:scale-105"
                              }`}
                            style={{ backgroundColor: color.value }}
                            title={color.name}
                          />
                        ))}
                        <div className="hidden sm:flex gap-1.5">
                          {colorOptions.slice(4).map((color) => (
                            <button
                              key={color.value}
                              onClick={() => setPenColor(color.value)}
                              className={`w-6 h-6 rounded-full border-2 transition-all ${penColor === color.value
                                  ? "border-white ring-2 ring-gray-300 scale-110"
                                  : "border-gray-300 hover:border-gray-400 hover:scale-105"
                                }`}
                              style={{ backgroundColor: color.value }}
                              title={color.name}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (whiteboardRef.current) {
                        const ctx = whiteboardRef.current.getContext("2d");
                        if (ctx) {
                          ctx.clearRect(0, 0, whiteboardRef.current.width, whiteboardRef.current.height);
                        }
                      }
                    }}
                    className="p-2 bg-red-100 text-red-600 hover:bg-red-200 rounded transition-all">
                    <RotateCcw className="w-4 h-4" />
                  </button>
                  <button onClick={() => setShowWhiteboard(false)} className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 p-2 sm:p-4 overflow-hidden">
                <canvas
                  ref={whiteboardRef}
                  className={`w-full h-full border border-gray-300 rounded-lg bg-white touch-none ${drawingTool === "eraser" ? "cursor-none" : "cursor-crosshair"
                    }`}
                  style={
                    drawingTool === "eraser"
                      ? {
                        cursor: `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='${eraserSize}' height='${eraserSize}' viewBox='0 0 ${eraserSize} ${eraserSize}'><circle cx='${eraserSize / 2
                          }' cy='${eraserSize / 2}' r='${eraserSize / 2 - 1}' fill='none' stroke='%23ef4444' stroke-width='2'/></svg>") ${eraserSize / 2
                          } ${eraserSize / 2}, auto`,
                      }
                      : {}
                  }
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setIsDrawing(true);
                    const canvas = whiteboardRef.current;
                    if (!canvas) return;
                    const rect = canvas.getBoundingClientRect();
                    const scaleX = canvas.width / (rect.width * window.devicePixelRatio);
                    const scaleY = canvas.height / (rect.height * window.devicePixelRatio);
                    const x = (e.clientX - rect.left) / scaleX;
                    const y = (e.clientY - rect.top) / scaleY;
                    const ctx = canvas.getContext("2d");
                    if (ctx) {
                      ctx.beginPath();
                      ctx.moveTo(x, y);
                    }
                  }}
                  onMouseMove={(e) => {
                    if (!isDrawing) return;
                    e.preventDefault();
                    const canvas = whiteboardRef.current;
                    if (!canvas) return;
                    const rect = canvas.getBoundingClientRect();
                    const scaleX = canvas.width / (rect.width * window.devicePixelRatio);
                    const scaleY = canvas.height / (rect.height * window.devicePixelRatio);
                    const x = (e.clientX - rect.left) / scaleX;
                    const y = (e.clientY - rect.top) / scaleY;
                    const ctx = canvas.getContext("2d");
                    if (ctx) {
                      ctx.lineTo(x, y);
                      if (drawingTool === "pen") {
                        ctx.globalCompositeOperation = "source-over";
                        ctx.strokeStyle = penColor;
                        ctx.lineWidth = penSize;
                      } else {
                        ctx.globalCompositeOperation = "destination-out";
                        ctx.lineWidth = eraserSize;
                      }
                      ctx.lineCap = "round";
                      ctx.lineJoin = "round";
                      ctx.stroke();
                    }
                  }}
                  onMouseUp={() => setIsDrawing(false)}
                  onMouseLeave={() => setIsDrawing(false)}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {alertModal.isOpen &&
        createPortal(
          <AlertModal
            isOpen={alertModal.isOpen}
            onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
            title={alertModal.title}
            message={alertModal.message}
            type={alertModal.type}
          />,
          document.body
        )}

      <SolutionVideoModal
        isOpen={isSolutionVideoModalOpen}
        onClose={handleCloseSolutionModal}
        question={selectedQuestionForSolution}
        questionIndex={currentQuestionIndex}
      />

      <Modal isOpen={isResultsModalOpen} onClose={() => setIsResultsModalOpen(false)} title="Sınav Sonuçları" size="lg">
        <div className="space-y-4">
          {(() => {
            const { totalQuestions, correctAnswers, wrongAnswers, unanswered, successRate } = calculateExamStats();

            if (!isExamCompleted) {
              return null;
            }

            return (
              <>
                <div className="text-center mb-6">
                  <div className="relative mb-4">
                    <div className="w-24 h-24 mx-auto rounded-full bg-white flex items-center justify-center">
                      <svg className="w-24 h-24 absolute transform -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="40" stroke="#e5e7eb" strokeWidth="8" fill="white" />
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          stroke={successRate >= 80 ? "#10b981" : successRate >= 60 ? "#f59e0b" : "#ef4444"}
                          strokeWidth="8"
                          fill="none"
                          strokeLinecap="round"
                          strokeDasharray={`${2 * Math.PI * 40}`}
                          strokeDashoffset={`${2 * Math.PI * 40 * (1 - successRate / 100)}`}
                          className="transition-all duration-1000 ease-out"
                        />
                      </svg>
                      <div className="relative z-10 text-center">
                        <div
                          className={`text-lg font-bold ${successRate >= 80 ? "text-green-600" : successRate >= 60 ? "text-yellow-600" : "text-red-600"
                            }`}>
                          %{successRate}
                        </div>
                        <div className="text-sm text-gray-500 -mt-0.5">başarı</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-sm font-medium text-green-700">Doğru Cevaplar</span>
                    </div>
                    <div className="text-lg font-bold text-green-800">{correctAnswers}</div>
                  </div>

                  <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <X className="w-4 h-4 text-red-600" />
                      </div>
                      <span className="text-sm font-medium text-red-700">Yanlış Cevaplar</span>
                    </div>
                    <div className="text-lg font-bold text-red-800">{wrongAnswers}</div>
                  </div>

                  <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <Circle className="w-4 h-4 text-gray-500" />
                      </div>
                      <span className="text-sm font-medium text-gray-700">Boş Cevaplar</span>
                    </div>
                    <div className="text-lg font-bold text-gray-800">{unanswered}</div>
                  </div>

                  <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <ClipboardCheck className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-medium text-blue-700">Toplam Soru</span>
                    </div>
                    <div className="text-lg font-bold text-blue-800">{totalQuestions}</div>
                  </div>
                </div>

                <div className="mt-6 text-center">
                  <div
                    className={`text-sm font-medium px-4 py-3 rounded-full inline-block shadow-sm ${successRate >= 80
                        ? "bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200"
                        : successRate >= 60
                          ? "bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-700 border border-yellow-200"
                          : "bg-gradient-to-r from-red-100 to-pink-100 text-red-700 border border-red-200"
                      }`}>
                    {successRate >= 80
                      ? "🎉 Mükemmel Performans!"
                      : successRate >= 60
                        ? "👍 İyi Çalışma!"
                        : successRate >= 40
                          ? "📚 Daha İyi Olabilir!"
                          : "💪 Tekrar Deneyelim!"}
                  </div>
                  <div className="text-sm text-gray-500 mt-3">
                    {successRate >= 80
                      ? "Tebrikler! Harika bir sonuç."
                      : successRate >= 60
                        ? "Güzel! Biraz daha çalışarak daha da iyisini yapabilirsin."
                        : successRate >= 40
                          ? "Fena değil! Biraz daha odaklanarak gelişebilirsin."
                          : "Sorun değil! Tekrar deneyerek daha iyi sonuçlar alabilirsin."}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </Modal>

      {selectedGainForTutor && (
        <DeficiencyTutorPanel
          isOpen={isTutorModalOpen}
          onClose={() => {
            setIsTutorModalOpen(false);
            setSelectedGainForTutor(null);
          }}
          gainId={selectedGainForTutor}
          allGains={contentGains}
          onGainResolved={async () => {
            try {
              const response = await ApiService.getUserGains({
                contentId: content.id,
                isResolved: false,
              });
              setContentGains(response?.data || []);
            } catch (err) {
              setContentGains([]);
            }
          }}
        />
      )}
    </div>
  );
}
